import {
  faChartLine,
  faGear,
  faHeartbeat,
  faHospital,
  faLifeRing,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  KeyboardShortcutsModal,
  KeyboardShortcutsTrigger
} from '@/components/KeyboardShortcutsHelp';
import Logout from '@/components/Logout/Logout';
import OpenInNewTabLink from '@/components/OpenInNewTabLink/OpenInNewTabLink';
import SuggestionsLink from '@/components/Support/SuggestionsLink';
import SupportLink from '@/components/Support/SupportLink';
import AppContext from '@/contexts/appContext';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import Dropdown from '@/ui/components/Dropdown';

import type { Props } from './types';

/**
 * The account menu — support links, settings, profile and logout.
 *
 * It is rendered from the mobile top bar behind the avatar icon, and from the desktop sidebar
 * behind the signed-in user's name, so the two never drift apart.
 */
const ProfileMenu = ({
  buttonText,
  buttonAriaLabel = 'Account menu',
  originUrl,
  customButtonClassName,
  customWrapperClassName,
  customMenuClassName,
  showChevronIcon = false
}: Props) => {
  const navigate = useNavigate();
  const { navigateToSettings } = useNavigateToPage();
  const { currentUser } = useContext(AppContext);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  return (
    <>
      <Dropdown
        buttonAriaLabel={buttonAriaLabel}
        buttonText={buttonText}
        customButtonClassName={customButtonClassName}
        customWrapperClassName={customWrapperClassName}
        customMenuClassName={customMenuClassName}
        items={[
          <OpenInNewTabLink key="open-in-new-tab" />,
          <SupportLink originUrl={originUrl} key="support-link" />,
          <SuggestionsLink originUrl={originUrl} key="suggestions-link" />,
          <a
            key="help-feature-request"
            href={`https://docs.google.com/forms/d/e/1FAIpQLSfWbGaARveokGsZsVLWjuOgaNNT4_iQ1FM9ABSmP0__gKE-xQ/viewform?usp=pp_url&entry.876110732=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noreferrer nofollow">
            <FontAwesomeIcon className="mr-2" icon={faLifeRing} />
            Engineering Ticket / Feature Request
          </a>,
          <a
            key="bi-request"
            href="https://docs.google.com/forms/d/e/1FAIpQLSc37hl_gLTaMevxKneVtwkOCF-Q-a8sFe9d2xm7_MiG8zX9yA/viewform"
            target="_blank"
            rel="noreferrer nofollow">
            <FontAwesomeIcon className="mr-2" icon={faChartLine} />
            BI Request
          </a>,
          <a
            key="emergency"
            className="mp-sensitive"
            href={`https://routines.affect.com/form/7ae44544-6bc1-42cf-98b5-a8b24b094c20?${encodeURIComponent(
              'What is your email?'
            )}=${encodeURIComponent(currentUser?.email || '')}`}
            target="_blank"
            rel="noreferrer nofollow">
            <FontAwesomeIcon className="mr-2" icon={faHospital} />
            Emergency Concern
          </a>,
          <div className="border-b border-gray-200" key="divider-status" />,
          <a
            key="system-status"
            href="https://affect.statuspage.io/"
            target="_blank"
            rel="noreferrer nofollow">
            <FontAwesomeIcon className="mr-2" icon={faHeartbeat} />
            System Status
          </a>,
          <button onClick={navigateToSettings} key="settings-link" type="button">
            <FontAwesomeIcon className="mr-2" icon={faGear} />
            Settings
          </button>,
          <KeyboardShortcutsTrigger
            key="keyboard-shortcuts"
            onClick={() => setShowKeyboardShortcuts(true)}
          />,
          <div className="border-b border-gray-200" key="divider" />,
          <button onClick={() => navigate('/my-profile')} key="my-profile" type="button">
            <FontAwesomeIcon className="mr-2" icon={faUser} />
            My Profile
          </button>,
          <Logout key="log-out" />
        ]}
        showChevronIcon={showChevronIcon}
      />

      <KeyboardShortcutsModal
        open={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />
    </>
  );
};

export default React.memo(ProfileMenu);
